import { ZoomDialog } from './zoom-dialog.js';

/**
 * Extends the theme's ZoomDialog component (used for product image zoom)
 * to add left/right arrow navigation, for use in the Instagram Reels popup.
 * All open/close/thumbnail-sync/keyboard-escape behavior is inherited
 * unchanged from ZoomDialog.
 */
export class InstagramZoomDialog extends ZoomDialog {
  /**
   * Shows the caption matching the given index, hides the rest.
   * @param {number} index
   */
  #showCaption(index) {
    const captions = this.querySelectorAll('[data-caption-index]');
    captions.forEach((el) => {
      el.hidden = Number(el.dataset.captionIndex) !== index;
    });
  }

  /**
   * @param {number} index
   * @param {PointerEvent} event
   */
  open(index, event) {
    this.#showCaption(index);
    return super.open(index, event);
  }

  /**
   * @param {number} index
   * @param {Object} options
   */
  selectThumbnail(index, options) {
    this.#showCaption(index);
    return super.selectThumbnail(index, options);
  }

  /**
   * Finds the index of the currently active thumbnail/media.
   * @returns {number}
   */
  #activeIndex() {
    const { thumbnails } = this.refs;
    if (!thumbnails) return 0;
    const buttons = Array.from(thumbnails.querySelectorAll('button'));
    const index = buttons.findIndex((button) => button.getAttribute('aria-selected') === 'true');
    return index === -1 ? 0 : index;
  }

  /**
   * Pauses any playing video inside the currently active slide,
   * so audio doesn't keep playing after navigating away from it.
   */
  #pauseActiveVideo() {
    const { media } = this.refs;
    const activeIndex = this.#activeIndex();
    const video = media?.[activeIndex]?.querySelector('video');
    if (video && !video.paused) video.pause();
  }

  /**
   * Advances to the next reel.
   * @param {PointerEvent} [event]
   */
  next(event) {
    event?.preventDefault?.();
    const { thumbnails } = this.refs;
    if (!thumbnails) return;
    this.#pauseActiveVideo();
    const total = thumbnails.children.length;
    const nextIndex = (this.#activeIndex() + 1) % total;
    this.handleThumbnailClick(nextIndex);
  }

  /**
   * Goes back to the previous reel.
   * @param {PointerEvent} [event]
   */
  previous(event) {
    event?.preventDefault?.();
    const { thumbnails } = this.refs;
    if (!thumbnails) return;
    this.#pauseActiveVideo();
    const total = thumbnails.children.length;
    const prevIndex = (this.#activeIndex() - 1 + total) % total;
    this.handleThumbnailClick(prevIndex);
  }

  /**
   * Also pause video on close, so it doesn't keep playing in the background.
   */
  async close() {
    this.#pauseActiveVideo();
    await super.close();
  }
}

if (!customElements.get('instagram-zoom-dialog')) {
  customElements.define('instagram-zoom-dialog', InstagramZoomDialog);
}